import { useEffect, useRef, useState } from 'react';

// PrimeReact components
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';

// App components
import { LettersTab } from '../../LettersTab';
import { CancellationTab } from '../CancellationLetterTab';
import Loader from '../../../../components/Loadeer/Loader';

// Config / Services
import { getAllDetailsDirect, getSpecificDetailsById, postAllData } from '../../../../service/ProductService';
import { DOWNLOADCANCELLATIONLETTER, CANCELLATIONLETTER, STORECANCELLATIONLETTER } from '../../../../config/ApiConstant';
import { councilOptions, defaultPageRowOptions, defaultRowOptions, SkeletonbodyTemplate, Skeletonitems } from '../../../../appconfig/Settings';
import { WrongAffidavitLetterListColumns } from '../../../../appconfig/DatatableSetting';
import { amountTemplate, dateTemplate } from '../../../../appconfig/DatatableTemplateSetting';
import { LetterShowClick, validateWrongAffidavitLetterForm } from '../../../../config/Validate';

interface WrongAffidavitData {
    Name?: string | null;
    ToAddress?: string | null;
    CouncilName?: string | null;
    RegNo?: number | null
}

export const WrongAffidavitLetter = () => {
    document.title = 'Wrong Affidavit Letter | GSPC';

    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    const [pageLoad, setPageLoad] = useState<boolean>(true);
    const [errors, setErrors] = useState<any>({});
    const [regNo, setRegNo] = useState<string>('');
    const [wrongAffidavitLetterData, setWrongAffidavitLetterData] = useState<WrongAffidavitData>({
        CouncilName: null
    });
    const [addLoader, setAddLoader] = useState<boolean>(false);
    const [wrongAffidavitLetterList, setWrongAffidavitLetterList] = useState<any[]>([]);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [buttonVisible, setButtonVisible] = useState<boolean>(true);

    useEffect(() => {
        getWrongAffidavitLetters();
    }, []);

    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    const rightToolbarTemplate = () => (
        <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAllFilter()} className="p-button-outlined" />
    );

    const clearAllFilter = () => {
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    const clearAll = () => {
        setRegNo('');
        setWrongAffidavitLetterData({
            Name: null,
            ToAddress: null,
            CouncilName: null,
        });
        setErrors({});
        setButtonVisible(true);
    };

    const getWrongAffidavitLetters = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=Wrong Affidavit&search=');
            if (response) {
                const { List } = response;
                setWrongAffidavitLetterList(List || []);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load data' });
        } finally {
            setPageLoad(true);
        }
    };

    const getUserDetails = async () => {
        try {
            const { errors, isError } = LetterShowClick(regNo);
            setErrors(errors);
            if (!isError) {
                setSearchLoading(true);
                const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=Wrong Affidavit&FileNo=' + regNo);
                if (response && response.Details) {
                    const { StudF, StudM, StudL, postAddress, RegNo, postDist, postPIN, PPhone } = response.Details;
                    let FromNm = `${StudL || ''} ${StudF || ''} ${StudM || ''}`.trim();
                    let address =
                        (!window.cn(postAddress) ? postAddress : "") +
                        ", " +
                        (!window.cn(postDist) ? postDist : "") +
                        "-" +
                        (!window.cn(postPIN) ? postPIN : "") +
                        ", " +
                        (!window.cn(PPhone) ? "Mobile-" + PPhone : "");
                    setWrongAffidavitLetterData({
                        ...wrongAffidavitLetterData,
                        Name: FromNm,
                        RegNo: RegNo,
                        ToAddress: address,
                    });
                    setButtonVisible(false);
                } else {
                    setButtonVisible(true);
                }
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load data' });
        } finally {
            setSearchLoading(false);
        }
    };

    const onInputChange = (e: any, name: string) => {
        const val = e?.target?.value ?? e?.value ?? e;
        setWrongAffidavitLetterData((prev) => ({ ...prev, [name]: val }));
    };

    // Handle Wrong Affidavit Letter Submission
    const addWrongAffidavitLetterData = async () => {
        try {
            const { errors, isError } = validateWrongAffidavitLetterForm(regNo, wrongAffidavitLetterData);
            setErrors(errors);
            if (isError) return;

            const requestData = {
                FileNo: regNo,
                Name: wrongAffidavitLetterData.Name || '',
                cancelby: "Wrong Affidavit",
                RegNo: wrongAffidavitLetterData.RegNo || '',
                ToAddress: wrongAffidavitLetterData.ToAddress || '',
                council_name: wrongAffidavitLetterData.CouncilName || '',
            };

            setAddLoader(true);

            // post api
            const response = await postAllData(STORECANCELLATIONLETTER, requestData);
            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Wrong Affidavit Letter saved successfully' });
                clearAll();
                getWrongAffidavitLetters();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong while saving' });
        } finally {
            setAddLoader(false);
        }
    };

    const actionsTemplate = (rowData: any) => (
        <div className="actions action-btn">
            <Button icon="pi pi-download" onClick={() => downloadLetter(rowData?.SrNo)} tooltip="Download" tooltipOptions={{ position: 'top' }} />
        </div>
    );

    const downloadLetter = async (id: any) => {
        try {
            setLoading(true);
            const response = await getSpecificDetailsById(DOWNLOADCANCELLATIONLETTER, id);
            if (response?.file_url) {
                const fileName = `wrong-affidavit-letter-${new Date().getTime()}.pdf`;
                const link = document.createElement('a');
                link.href = response.file_url;
                link.target = '_blank';
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Download failed' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <LettersTab mainTabLabel="Cancellation Letters" />
            <CancellationTab initialTabLabel="Wrong Affidavit Letter" />
            <Toast ref={toast} />

            <div className="grid crud-demo mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="p-fluid formgrid grid align-items-center">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="regNo">File No <span style={{ color: 'red' }}>*</span></label>
                                <InputText
                                    value={regNo}
                                    name="regNo"
                                    keyfilter="num"
                                    autoComplete="off"
                                    placeholder="Enter File No."
                                    onChange={(e) => setRegNo(e.target.value)}
                                    className={errors['regNo'] && 'p-invalid'}
                                />
                                <small className="p-invalid-txt">{errors['regNo']}</small>
                            </div>
                            <div className="field col-12 md:col-1 mt-4">
                                <Button label="Search" className="p-button-primary" onClick={getUserDetails} loading={searchLoading} />
                            </div>
                        </div>

                        <hr />

                        <div className="p-fluid formgrid grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="Name">Name</label>
                                <InputText value={wrongAffidavitLetterData?.Name || ''} name="Name" disabled />
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="CouncilName">Council <span style={{ color: 'red' }}>*</span></label>
                                <Dropdown
                                    value={wrongAffidavitLetterData?.CouncilName}
                                    options={councilOptions}
                                    onChange={(e) => onInputChange(e, 'CouncilName')}
                                    placeholder="Select Council"
                                    className={errors['CouncilName'] && 'p-invalid'}
                                />
                                <small className="p-invalid-txt">{errors['CouncilName']}</small>
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="ToAddress">Address</label>
                                <InputTextarea value={wrongAffidavitLetterData?.ToAddress || ''} rows={4} onChange={(e) => onInputChange(e, 'ToAddress')} />
                            </div>
                        </div>

                        <div className="formgrid grid">
                            <div className="field col-12 md:col-12" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary" onClick={clearAll} />
                                <Button label="Save" icon="pi pi-check" onClick={addWrongAffidavitLetterData} loading={addLoader} disabled={buttonVisible} />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {pageLoad ? (
                            <DataTable
                                value={wrongAffidavitLetterList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={wrongAffidavitLetterList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                emptyMessage="No Data Found"
                                removableSort
                            >
                                {WrongAffidavitLetterListColumns.map((col) => {
                                    if (col.field === 'action') return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                    if (col.field === 'created_at') return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} />;
                                    if (col.field === 'amount') return <Column key={col.field} field={col.field} header={col.header} body={amountTemplate} />;
                                    return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {WrongAffidavitLetterListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            {loading && <Loader />}
        </>
    );
};
